import React, { useCallback } from 'react';

import {
  Avatar,
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogContent,
  makeStyles,
  Typography,
} from '@material-ui/core';

import * as socketEvents from 'constants/socketEvents';
import socket from 'services/socket';
import { useAppDispatch, useAppSelector } from 'store';
import {
  AtualizarReacoesMensagemInterface,
  fetchReacoesMensagem,
  mensagensSelector,
  removerReacao,
  resetReacoesMensagem,
} from 'store/reducers/mensagens';

import DialogHeader from './DialogHeader';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 12,
    width: '100%',
  },
  avatar: {
    marginRight: 8,
  },
});

type ModalReacoesProps = {
  mensagemId: string;
  open: boolean;
  desativarInteracao?: boolean;
  onClose: () => void;
};

export default function ModalReacoes(props: ModalReacoesProps) {
  const { open, onClose, mensagemId, desativarInteracao = false } = props;

  const classes = useStyles();

  const dispatch = useAppDispatch();

  const { reacoesMensagem, loadingReacoesMensagem } =
    useAppSelector(mensagensSelector);

  const handleRemoverReacao = () => {
    dispatch(removerReacao({ mensagemId }));
  };

  const getReacoes = React.useCallback(() => {
    dispatch(fetchReacoesMensagem({ mensagemId }));
  }, [dispatch, mensagemId]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    const onReacaoMensagem = (data: AtualizarReacoesMensagemInterface) => {
      const { lastReactions } = data.reactions;

      if (lastReactions && lastReactions.length === 0) {
        handleClose();
        return;
      }

      if (data.reactions.msgReagidaId === mensagemId) {
        dispatch(fetchReacoesMensagem({ mensagemId }));
      }
    };

    socket.on(socketEvents.ON_REACAO_MENSAGEM, onReacaoMensagem);

    return () => {
      socket.off(socketEvents.ON_REACAO_MENSAGEM, onReacaoMensagem);
    };
  }, [dispatch, mensagemId, handleClose]);

  React.useEffect(() => {
    if (open) {
      getReacoes();
    }
  }, [dispatch, getReacoes, mensagemId, open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionProps={{
        onExited: () => {
          dispatch(resetReacoesMensagem());
        },
      }}
    >
      <DialogHeader
        title="Reações"
        description={`${reacoesMensagem.length} ${
          reacoesMensagem.length === 1 ? 'reação' : 'reações'
        }`}
        onClose={onClose}
      />

      <DialogContent>
        {loadingReacoesMensagem && (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress color="inherit" size={24} />
          </Box>
        )}

        {!loadingReacoesMensagem &&
          reacoesMensagem.map(reacao => (
            <ButtonBase
              key={reacao.id}
              className={classes.content}
              disabled={!reacao.atendente || desativarInteracao}
              onClick={handleRemoverReacao}
            >
              <Box display="flex" alignItems="center" flexDirection="row">
                <Avatar
                  className={classes.avatar}
                  src={reacao?.contato?.foto || reacao?.atendente?.foto}
                />

                <Box
                  marginRight={2}
                  display="flex"
                  flexDirection="column"
                  alignItems="flex-start"
                >
                  <Typography variant="body2">
                    {reacao?.contato?.nome || reacao?.atendente?.nome}
                  </Typography>

                  <Typography
                    color="textSecondary"
                    style={{ lineHeight: 1 }}
                    variant="caption"
                  >
                    {reacao?.contato?.contato ||
                      (!desativarInteracao && 'Clique para remover a reação')}
                  </Typography>
                </Box>
              </Box>

              <Typography variant="h5">
                {reacao.reacao === 'love' ? '🤎' : reacao.reacao}
              </Typography>
            </ButtonBase>
          ))}
      </DialogContent>
    </Dialog>
  );
}
